// @ts-check
/* eslint-env browser */

import { reactiveStore } from './reactive-store.js'
import { snapshotOverlayGcps, restoreOverlayGcps } from './base-map/geotiff-overlay.js'

/**
 * @typedef {import('./base-map/geotiff-overlay.js').OverlayGcpSnapshot} OverlayGcpSnapshot
 * @typedef {{ properties?: { _id?: string } }} MapHistoryFeature
 * @typedef {{ features: MapHistoryFeature[], overlayGcps: OverlayGcpSnapshot[] }} MapHistoryState
 */

/**
 * @param {unknown[]} features
 * @returns {MapHistoryState}
 */
function createMapState (features) {
  const mapFeatures = /** @type {MapHistoryFeature[]} */ (features)
  return {
    features: mapFeatures,
    overlayGcps: snapshotOverlayGcps()
  }
}

class UndoRedo extends HTMLElement {
  connectedCallback () {
    this.#setupClickHandlers()
    this.#setupKeyboardShortcuts()

    reactiveStore.features.subscribe((features, message) => {
      // Skip programmatic updates that shouldn't be recorded in history
      if (message === 'undo-redo' || message === 'sync') {
        return
      }
      const state = createMapState(features)
      if (!this.historyManager) {
        this.historyManager = new HistoryManager(state)
      } else {
        this.#update(this.historyManager.addState(state))
      }
    })
  }

  /**
   * Sets up keyboard shortcuts for undo/redo
   */
  #setupKeyboardShortcuts () {
    document.addEventListener('keydown', (e) => {
      // Skip if user is on an input element
      const target = e.target
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return
      }

      const isCtrlOrCmd = e.ctrlKey || e.metaKey

      // Ctrl/Cmd+Z for undo
      if (isCtrlOrCmd && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault()
        this.#update(this.historyManager?.undo())
      // Ctrl/Cmd+Shift+Z for redo
      } else if (isCtrlOrCmd && e.key.toLowerCase() === 'z' && e.shiftKey) {
        e.preventDefault()
        this.#update(this.historyManager?.redo())
      }
    })
  }

  /**
   * Sets up click handlers for undo/redo buttons
   */
  #setupClickHandlers () {
    this.querySelector('[data-action="undo"]')?.addEventListener('click', () => {
      this.#update(this.historyManager?.undo())
    })
    this.querySelector('[data-action="redo"]')?.addEventListener('click', () => {
      this.#update(this.historyManager?.redo())
    })
  }

  /**
    * Updates the reactiveStore and undo/redo button states
    * @param {*} newData - Object containing the new state and the lengths of the undo/redo stacks.
    * @returns {void}
    */
  #update (newData) {
    if (!newData) {
      return
    }
    if (newData.state) {
      const { features, overlayGcps } = /** @type {MapHistoryState} */ (newData.state)

      // If the selected feature no longer exists after undo/redo, deselect it
      const currentId = reactiveStore.selectedFeatureId.get()
      if (currentId && !features.some((f) => f?.properties?._id === currentId)) {
        reactiveStore.selectedFeatureId.set('')
      }

      restoreOverlayGcps(overlayGcps)
      reactiveStore.features.set(features, 'undo-redo')
    }
    /** @type {HTMLButtonElement | null} */
    const undoBtn = this.querySelector('[data-action="undo"]')
    /** @type {HTMLButtonElement | null} */
    const redoBtn = this.querySelector('[data-action="redo"]')
    if (!undoBtn || !redoBtn) {
      return
    }
    undoBtn.classList.toggle('govuk-!-display-none', newData.undoStackLength === 0)
    redoBtn.classList.toggle('govuk-!-display-none', newData.redoStackLength === 0)
    undoBtn.disabled = newData.undoStackLength === 0
    redoBtn.disabled = newData.redoStackLength === 0
    // Move focus to the other button if one is disabled and it had focus
    if (undoBtn.disabled && document.activeElement === document.body) {
      redoBtn.focus()
    } else if (redoBtn.disabled && document.activeElement === document.body) {
      undoBtn.focus()
    }
  }
}

customElements.define('undo-redo', UndoRedo)

export class HistoryManager {
  #MAX_UNDO_STACK_LENGTH = 100

  constructor (initialState) {
    this.undoStack = []
    this.redoStack = []
    this.state = initialState
  }

  // Record a new state (after user action)
  addState (newState) {
    // Deep-clone to prevent mutations from corrupting history
    this.undoStack.push(structuredClone(this.state))
    if (this.undoStack.length > this.#MAX_UNDO_STACK_LENGTH) {
      this.undoStack.shift()
    }
    this.state = newState
    this.redoStack = []
    return {
      undoStackLength: this.undoStack.length,
      redoStackLength: this.redoStack.length
    }
  }

  undo () {
    if (this.undoStack.length === 0) {
      return
    }
    this.redoStack.push(structuredClone(this.state))
    this.state = this.undoStack.pop()
    return {
      state: structuredClone(this.state),
      undoStackLength: this.undoStack.length,
      redoStackLength: this.redoStack.length
    }
  }

  redo () {
    if (this.redoStack.length === 0) {
      return
    }
    this.undoStack.push(structuredClone(this.state))
    this.state = this.redoStack.pop()
    return {
      state: structuredClone(this.state),
      undoStackLength: this.undoStack.length,
      redoStackLength: this.redoStack.length
    }
  }

  clear () {
    this.undoStack = []
    this.redoStack = []
  }
}
